import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOW, YELLOW } from 'styles/tokens';

interface BulletGraphProps {
  label: string;
  value: number;
  target?: number;
  /** Upper bound of the scale; defaults to max(value, target) * 1.25 */
  max?: number;
  /** Optional ranges as luminosity bands (poor → acceptable → good). Values in same units. */
  bands?: [number, number];
  format?: (v: number) => string;
  caption?: ReactNode;
  /** When set, a "view leads" icon appears next to the label and invokes this handler. */
  onNavigate?: () => void;
  /** Optional tooltip for the navigate icon. */
  navigationTooltip?: string;
}

const BulletGraph: FC<BulletGraphProps> = ({
  label,
  value,
  target,
  max,
  bands,
  format = (v) => v.toLocaleString(),
  caption,
  onNavigate,
  navigationTooltip,
}) => {
  const scaleMax = max ?? (Math.max(value, target ?? 0) * 1.25 || 1);
  const valuePct = Math.min(100, (value / scaleMax) * 100);
  const targetPct = target !== undefined ? Math.min(100, (target / scaleMax) * 100) : null;
  const [poor, acceptable] = bands ?? [scaleMax * 0.33, scaleMax * 0.66];
  const poorPct = Math.min(100, (poor / scaleMax) * 100);
  const acceptablePct = Math.min(100, (acceptable / scaleMax) * 100);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: `${NAVY}8C`,
            }}
          >
            {label}
          </Typography>
          {onNavigate && (
            <Tooltip title={navigationTooltip || 'Ver leads'} arrow>
              <IconButton
                size="small"
                onClick={onNavigate}
                aria-label={navigationTooltip || `Ver leads de ${label}`}
                sx={{
                  width: 22,
                  height: 22,
                  color: `${NAVY}80`,
                  backgroundColor: 'transparent',
                  transition: 'color 0.15s ease, background-color 0.15s ease',
                  '&:hover': { color: NAVY, backgroundColor: `${NAVY}0d` },
                }}
              >
                <ManageSearchIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              fontSize: '1.15rem',
              color: NAVY,
              lineHeight: 1,
            }}
          >
            {format(value)}
          </Typography>
          {target !== undefined && (
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.75rem',
                color: `${NAVY}80`,
              }}
            >
              / {format(target)}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          height: 14,
          borderRadius: RADIUS_ITEM,
          overflow: 'hidden',
          backgroundColor: SURFACE_CONTAINER_LOW,
        }}
      >
        {/* Luminosity bands: poor (lightest) → acceptable → good (darkest) */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to right,
              ${NAVY}0F 0%,
              ${NAVY}0F ${poorPct}%,
              ${NAVY}1A ${poorPct}%,
              ${NAVY}1A ${acceptablePct}%,
              ${NAVY}26 ${acceptablePct}%,
              ${NAVY}26 100%)`,
          }}
        />
        {/* Current value bar */}
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            bottom: '25%',
            left: 0,
            width: `${valuePct}%`,
            backgroundColor: NAVY,
            borderRadius: RADIUS_ITEM,
            transition: 'width 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)',
          }}
        />
        {/* Target tick */}
        {targetPct !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${targetPct}%`,
              width: 3,
              backgroundColor: YELLOW,
              transform: 'translateX(-1.5px)',
              borderRadius: 2,
            }}
          />
        )}
      </Box>

      {caption && (
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.75rem',
            color: `${NAVY}80`,
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};

export default BulletGraph;
